import { motion } from "framer-motion";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Quote } from "lucide-react";

const testimonials = [
  {
    quote: "CivicResolve has transformed our public works department. We're resolving issues 50% faster and the public feedback has been incredible.",
    author: "Jane Doe",
    role: "Head of Public Works, City of Everbright",
    avatar: "https://storage.googleapis.com/fenado-ai-farm-public/generated/6db4d3f0-6eae-4319-873f-cfa2dbcf4786.png"
  },
  {
    quote: "The ability to see photos and the exact location on a map is a game-changer. No more guesswork. This is the future of civic management.",
    author: "John Smith",
    role: "City Manager, Springfield District",
    avatar: "https://storage.googleapis.com/fenado-ai-farm-public/generated/3759c630-4244-4e09-b219-9336adee70ef.png"
  }
];

const TestimonialsSection = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-background via-secondary/5 to-background px-4 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Section header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            What Our <span className="text-gradient">Users Say</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Hear from municipal leaders who have transformed their issue management processes
          </p>
        </motion.div>
        
        {/* Testimonials grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <Card className="bg-card/70 backdrop-blur-sm border-border h-full hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="mb-6">
                      <Quote className="h-8 w-8 text-primary/30 mb-2" />
                      <p className="text-lg text-card-foreground italic">"{testimonial.quote}"</p>
                    </div>
                    
                    <div className="mt-auto flex items-center">
                      <Avatar className="h-12 w-12 mr-4">
                        <AvatarImage src={testimonial.avatar} alt={testimonial.author} />
                        <AvatarFallback className="bg-primary/20 text-primary">
                          {testimonial.author.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-card-foreground font-medium">{testimonial.author}</p>
                        <p className="text-muted-foreground text-sm">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
        
        {/* Stats section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          <div className="bg-card border border-border rounded-xl p-6 text-center">
            <p className="text-4xl font-bold text-primary mb-2">50%</p>
            <p className="text-muted-foreground">Faster issue resolution</p>
          </div>
          
          <div className="bg-card border border-border rounded-xl p-6 text-center">
            <p className="text-4xl font-bold text-primary mb-2">97%</p>
            <p className="text-muted-foreground">Customer satisfaction</p>
          </div>
          
          <div className="bg-card border border-border rounded-xl p-6 text-center">
            <p className="text-4xl font-bold text-primary mb-2">35%</p>
            <p className="text-muted-foreground">Reduction in operational costs</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;